import React, { useEffect, useRef } from 'react';
import { Mail, Tag, Check, Headphones, CreditCard } from 'lucide-react';
import { company } from '../mock';
import Tilt3D from './Tilt3D';

// Edit the text of the 5 steps here.
const steps = [
  {
    icon: Mail,
    title: 'Send inquiry',
    desc: `Email ${company.email} with your lane, cargo and dates — we reply within 24h, by email and WhatsApp.`,
    tag: 'You email us'
  },
  {
    icon: Tag,
    title: 'Instant quote',
    desc: 'An all-in rate back in minutes — freight, customs, delivery. Locked for 14 days.',
    tag: 'We price it'
  },
  {
    icon: Check,
    title: 'Get booking',
    desc: 'You confirm; we secure carrier space and issue the booking number the same hour.',
    tag: 'You confirm'
  },
  {
    icon: Headphones,
    title: 'We handle it',
    desc: 'Your coordinator runs pickup, customs and transit — live milestones the whole way.',
    tag: 'We operate'
  },
  {
    icon: CreditCard,
    title: 'Payment done',
    desc: 'POD delivered, one clear invoice, paid your way. No hidden surcharges, ever.',
    tag: 'We close out'
  }
];

export default function HowItWorks() {
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add('in-view'));
    }, { threshold: 0.15 });
    ref.current?.querySelectorAll('.reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden py-24 bg-white" data-testid="how-it-works">
      <style>{`
        /* Glowing bar that slides along the dashed line, one step at a time (8s loop) */
        .hiw-glide {
          position: absolute;
          left: 0;
          top: -4px;
          width: 25%;              /* the line has 4 gaps, so 25% = the distance between two steps */
          height: 6px;
          border-radius: 9999px;
          background: linear-gradient(90deg, rgba(42,31,122,0) 0%, rgba(42,31,122,0.6) 50%, rgba(42,31,122,0) 100%);
          filter: blur(1.5px);
          opacity: 0;
          animation: hiw-glide 8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
          will-change: transform, opacity;
        }
        @keyframes hiw-glide {
          0%   { transform: translateX(0%);   opacity: 0; }
          5%   { transform: translateX(0%);   opacity: 1; }
          20%  { transform: translateX(0%);   opacity: 1; }   /* pause on step 1 -> 2 */
          30%  { transform: translateX(100%); opacity: 1; }
          45%  { transform: translateX(100%); opacity: 1; }   /* pause on step 2 -> 3 */
          55%  { transform: translateX(200%); opacity: 1; }
          70%  { transform: translateX(200%); opacity: 1; }   /* pause on step 3 -> 4 */
          80%  { transform: translateX(300%); opacity: 1; }
          95%  { transform: translateX(300%); opacity: 1; }   /* pause on step 4 -> 5 */
          100% { transform: translateX(300%); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hiw-glide { animation: none; opacity: 0; }
        }
      `}</style>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="reveal text-[color:var(--brand)] uppercase tracking-[0.4em] text-xs mb-4">How It Works</p>
          <h2 className="reveal text-4xl md:text-5xl font-light text-[color:var(--ink)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Five steps from inquiry to delivery
          </h2>
          <div className="reveal mx-auto w-16 h-[2px] bg-[color:var(--brand)] mb-6" />
          <p className="reveal text-[color:var(--muted)] leading-relaxed">
            One coordinator, one thread, zero surprises. Here is exactly how a shipment moves through {company.name}.
          </p>
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-12">
          {/* dashed connector line (desktop only) - runs through the middle of the icon tiles.
              The glowing bar inside it is exactly one step wide and slides step 1 -> 2 -> 3 -> 4 -> 5, then repeats. */}
          <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0 border-t-2 border-dashed border-[color:var(--line)]" aria-hidden="true">
            <span className="hiw-glide" />
          </div>

          {steps.map((s, i) => {
            const Ico = s.icon;
            return (
              <div
                key={s.title}
                className="reveal relative flex flex-col items-center text-center"
                style={{ transitionDelay: `${i * 0.1}s` }}
                data-testid={`how-step-${i + 1}`}
              >
                <div className="relative z-10 mb-6">
                  <Tilt3D max={16} glare={false} className="w-16 h-16 rounded-2xl bg-white border border-[color:var(--line)] shadow-[0_10px_30px_-10px_rgba(42,31,122,0.25)] flex items-center justify-center text-[color:var(--ink)]">
                    <Ico size={26} strokeWidth={1.6} />
                  </Tilt3D>
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[color:var(--brand)] text-white text-xs font-semibold flex items-center justify-center shadow-lg">
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-[color:var(--ink)] mb-2">{s.title}</h3>
                <p className="text-[color:var(--muted)] text-sm leading-relaxed mb-3 max-w-[15rem]">{s.desc}</p>
                <span className="text-xs font-semibold text-[color:var(--brand)]">{s.tag}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
